import React from "react";
import styled from "styled-components";
import { Box, Card, Typography, Divider, Button } from "@bca/ui-library";
import { useRouter } from "next/dist/client/router";

import { formatMoney } from "./formatMoney";

interface NotHighestBidderProps {
  bidAmount: number;
  reserveMet: boolean;
  myMaxProxy: number;
  bidHistory: any;
  userBidStatus: string;
}

interface BidPlaced {
  bidAmount: number;
  reserveMet: boolean;
  myMaxProxy: number;
  numberOfBids: number;
}

const ReserveMet = styled(Box)`
  border-radius: 4px;
`;

const ReserveNotMet = styled(Box)`
  border: 1px solid #64798c;
  border-radius: 4px;
`;

const NoBidPlaced: React.FunctionComponent<{}> = () => {
  return (
    <>
      <Typography
        color="#070b16"
        css={`
          font-weight: bold;
        `}
        m={0}
        mt={2}
        variant="h6"
      >
        Sold
      </Typography>
      <Typography
        color="grey-blue"
        css={`
          line-height: 1.36;
        `}
        mt={2}
        mb={1}
        variant="body"
      >
        This vehicle is no longer available.
      </Typography>
    </>
  );
};

const BidPlaced: React.FunctionComponent<BidPlaced> = ({
  bidAmount,
  reserveMet,
  myMaxProxy,
  numberOfBids,
}) => {
  return (
    <>
      <Typography variant="h6" color="primary-red" m={0} mb={1}>
        Unfortunately, you missed out
      </Typography>
      <Box
        display="flex"
        justifyContent="flex-start"
        alignItems="center"
        mb={1}
      >
        <Typography
          css={`
            font-size: 30px;
            font-weight: 500;
          `}
          m={0}
          mr={3}
        >
          {formatMoney(bidAmount)}
        </Typography>
        {reserveMet && (
          <ReserveMet backgroundColor="grey-blue" height={26} py={0} px="10px">
            <Typography
              variant="caption"
              color="primary-white"
              m={0}
              css={`
                font-size: 13px;
                line-height: 24px;
              `}
            >
              Reserve met
            </Typography>
          </ReserveMet>
        )}
        {!reserveMet && (
          <ReserveNotMet backgroundColor="white" height={26} py={0} px={"10px"}>
            <Typography
              variant="caption"
              color="grey-blue"
              m={0}
              css={`
                font-size: 13px;
                line-height: 24px;
              `}
            >
              Reserve not met
            </Typography>
          </ReserveNotMet>
        )}
      </Box>
      <Box display="flex">
        <Typography m={0} variant="body" color="grey-blue">
          {`${numberOfBids} ${numberOfBids === 1 ? "bid" : "bids"}`}
        </Typography>
        <>
          <Divider
            orientation="vertical"
            width="1px"
            marginTop={0}
            marginBottom={0}
            marginLeft={3}
            marginRight={3}
          />
          <Typography m={0} variant="body" color="grey-blue">
            {`Your Max Bid was ${formatMoney(myMaxProxy)}`}
          </Typography>
        </>
      </Box>
    </>
  );
};

const NotHighestBidder: React.FunctionComponent<NotHighestBidderProps> = ({
  bidAmount,
  reserveMet,
  userBidStatus,
  myMaxProxy,
  bidHistory,
}) => {
  const router = useRouter();

  const noBidPlaced = userBidStatus === "NoBids";
  const numberOfBids = bidHistory.length;

  return (
    <Card
      width={["100%", "100%", 620]}
      height="auto"
      marginX={0}
      marginY={0}
      paddingX={28}
      paddingY={28}
    >
      <Box display="flex" flexDirection={["column", "row"]}>
        <Box display="flex" flexDirection="column" flex={1} mb={[3, 0]}>
          {noBidPlaced ? (
            <NoBidPlaced />
          ) : (
            <BidPlaced
              bidAmount={bidAmount}
              reserveMet={reserveMet}
              myMaxProxy={myMaxProxy}
              numberOfBids={numberOfBids}
            />
          )}
        </Box>
        <Box
          display="flex"
          flex={1}
          alignItems={noBidPlaced ? "center" : "flex-end"}
        >
          <Button
            variant="secondary"
            text="Find more vehicles"
            onClick={() => {
              router.push("//auctionview.bca.co.uk/Buyer/FacetedSearch");
            }}
          />
        </Box>
      </Box>
    </Card>
  );
};

export default NotHighestBidder;
